/**
 * Step 4: Real Price Calculation (LOT 3)
 * 
 * Utilise le moteur de tarification pour calculer le prix réel
 * avec traçabilité complète et détail des règles appliquées.
 */
import React, { useState, useEffect } from 'react';
import { useBooking } from '../../context/BookingContext';
import { pricingAPI } from '../../services/bookingApi';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { 
  Calculator, MapPin, Clock, Car, 
  ArrowRight, ArrowLeft, AlertTriangle, Info,
  CheckCircle2, Tag, Percent, Moon, Sun,
  Calendar, MapPinned, ChevronDown, ChevronUp, Navigation
} from 'lucide-react';

const Step4Price = () => {
  const { bookingData, updateBookingData, nextStep, prevStep } = useBooking();
  const [priceResult, setPriceResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    calculateRealPrice();
  }, []);

  const calculateRealPrice = async () => {
    setLoading(true);
    setError(null);

    try {
      // Si c'est une mise à disposition (hourly), recalculer côté backend pour gérer les suppléments nuit/weekend
      if (bookingData.service_type === 'hourly') {
        const priceInput = {
          pickup_address: bookingData.pickup?.address || '',
          pickup_lat: bookingData.pickup?.latitude || 48.8566,
          pickup_lng: bookingData.pickup?.longitude || 2.3522,
          pickup_place_id: bookingData.pickup?.place_id || null,
          dropoff_address: bookingData.dropoff?.address || bookingData.pickup?.address || '',
          dropoff_lat: bookingData.dropoff?.latitude || bookingData.pickup?.latitude || 48.8566,
          dropoff_lng: bookingData.dropoff?.longitude || bookingData.pickup?.longitude || 2.3522,
          dropoff_place_id: bookingData.dropoff?.place_id || bookingData.pickup?.place_id || null,
          pickup_datetime: bookingData.pickup_datetime || new Date().toISOString(),
          vehicle_category: bookingData.vehicle_category || 'berline',
          service_type: 'hourly',
          passengers: bookingData.passengers || 1,
          luggage: bookingData.luggage || 0,
          is_round_trip: false,
          hours_booked: bookingData.hours_booked || null,
          options: bookingData.options || [],
          intermediate_stops: []
        };

        const result = await pricingAPI.calculatePrice(priceInput);

        if (result.success && result.price_details) {
          setPriceResult(result);
          updateBookingData({ 
            price_details: result.price_details,
            price_estimate: {
              total: result.price_details.total_ttc,
              is_provisional: false
            }
          });
          setLoading(false);
          return;
        }
      }

      // Legacy fallback (should rarely be used)
      if (bookingData.service_type === 'hourly' && bookingData.price_details) {
        const hourlyDetails = bookingData.price_details;
        
        // Construire le breakdown
        const breakdown = [
          { 
            label: `${hourlyDetails.vehicle_name || bookingData.vehicle_category} - ${hourlyDetails.duration_hours}h`,
            amount: hourlyDetails.base_price,
            is_total: false
          },
          { 
            label: `(${hourlyDetails.price_per_hour}€/h × ${hourlyDetails.duration_hours}h)`,
            amount: hourlyDetails.base_price,
            is_total: false,
            is_detail: true
          }
        ];

        // Ajouter km inclus
        if (hourlyDetails.km_included) {
          breakdown.push({
            label: `Km inclus`,
            amount: hourlyDetails.km_included,
            is_total: false,
            is_km: true
          });
        }

        // Ajouter km supplémentaires si présents
        if (hourlyDetails.extra_km && hourlyDetails.extra_km > 0) {
          breakdown.push({
            label: `Km supplémentaires (${hourlyDetails.extra_km} km × ${hourlyDetails.extra_km_price}€)`,
            amount: hourlyDetails.extra_km_charge,
            is_total: false,
            is_extra: true
          });
        }

        // Construire un résultat compatible avec l'affichage
        const result = {
          success: true,
          price_details: {
            pricing_method: { selected_type: 'HOURLY' },
            base_calculation: {
              base_price: hourlyDetails.base_price,
              price_per_hour: hourlyDetails.price_per_hour,
              duration_hours: hourlyDetails.duration_hours,
              km_included: hourlyDetails.km_included,
              extra_km: hourlyDetails.extra_km || 0,
              extra_km_charge: hourlyDetails.extra_km_charge || 0
            },
            total_ht: hourlyDetails.total_ht || (hourlyDetails.total_ttc / 1.1),
            total_ttc: hourlyDetails.total_ttc,
            surcharges: [],
            surcharges_total: 0,
            options_total: hourlyDetails.options_total || 0,
            breakdown_display: breakdown,
            inputs: {
              route: {
                estimated_distance_km: hourlyDetails.estimated_distance || hourlyDetails.km_included || 0,
                estimated_duration_min: (hourlyDetails.duration_hours || 0) * 60
              },
              datetime: {
                is_weekend: false,
                is_holiday: false,
                time_slot: 'day'
              }
            },
            version: 'hourly_v1',
            calculation_id: `hourly_${Date.now()}`
          }
        };

        // Ajouter TVA au breakdown
        const tva = result.price_details.total_ttc - result.price_details.total_ht;
        if (tva > 0) {
          result.price_details.breakdown_display.push({
            label: 'TVA (10%)',
            amount: Math.round(tva * 100) / 100,
            is_total: false
          });
        }

        // Total
        result.price_details.breakdown_display.push({
          label: 'Total TTC',
          amount: result.price_details.total_ttc,
          is_total: true
        });

        setPriceResult(result);
        updateBookingData({ 
          price_details: result.price_details,
          price_estimate: {
            total: result.price_details.total_ttc,
            is_provisional: false
          }
        });
        setLoading(false);
        return;
      }

      // Pour les transferts, appeler l'API de tarification
      const priceInput = {
        pickup_address: bookingData.pickup?.address || '',
        pickup_lat: bookingData.pickup?.latitude || 48.8566,
        pickup_lng: bookingData.pickup?.longitude || 2.3522,
        pickup_place_id: bookingData.pickup?.place_id || null,
        dropoff_address: bookingData.dropoff?.address || '',
        dropoff_lat: bookingData.dropoff?.latitude || 48.9566,
        dropoff_lng: bookingData.dropoff?.longitude || 2.4522,
        dropoff_place_id: bookingData.dropoff?.place_id || null,
        pickup_datetime: bookingData.pickup_datetime || new Date().toISOString(),
        vehicle_category: bookingData.vehicle_category || 'berline',
        service_type: bookingData.service_type || 'transfer',
        passengers: bookingData.passengers || 1,
        luggage: bookingData.luggage || 0,
        is_round_trip: bookingData.is_round_trip || false,
        hours_booked: bookingData.hours_booked || null,
        options: bookingData.options || [],
        intermediate_stops: bookingData.intermediate_stops || []
      };

      const result = await pricingAPI.calculatePrice(priceInput);

      if (result.success && result.price_details) {
        setPriceResult(result);
        updateBookingData({ 
          price_details: result.price_details,
          price_estimate: {
            total: result.price_details.total_ttc,
            is_provisional: false
          }
        });
      } else {
        setError(result.error_message || "Erreur lors du calcul du prix");
      }
    } catch (err) {
      console.error('Price calculation error:', err);
      setError(
        err.response?.data?.detail?.message || 
        err.response?.data?.message ||
        "Impossible de calculer le prix. Veuillez réessayer."
      );
    } finally {
      setLoading(false);
    }
  };

  const vehicleNames = {
    berline: 'Berline',
    suv: 'SUV Premium',
    van: 'Van'
  };

  const getPricingMethodIcon = (method) => {
    const methodUpper = (method || '').toUpperCase();
    switch (methodUpper) {
      case 'FIXED_ROUTE': return <Tag className="w-4 h-4" />;
      case 'ZONE_ROUTE':
      case 'ZONE_ZONE':
        return <MapPinned className="w-4 h-4" />;
      case 'HOURLY': return <Clock className="w-4 h-4" />;
      default: return <Car className="w-4 h-4" />;
    }
  };

  const getPricingMethodLabel = (method) => {
    const methodUpper = (method || '').toUpperCase();
    switch (methodUpper) {
      case 'FIXED_ROUTE': return 'Forfait fixe';
      case 'ZONE_ROUTE': 
      case 'ZONE_ZONE': 
        return 'Forfait zone';
      case 'HOURLY': return 'Mise à disposition';
      case 'KILOMETRIQUE': return 'Tarif au kilomètre';
      default: return 'Tarif au kilomètre';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Calculator className="w-12 h-12 text-[#C9A24D] mx-auto mb-4 animate-pulse" />
          <p className="text-[#E5E5E5]">Calcul du prix en cours...</p>
          <p className="text-[#E5E5E5]/60 text-sm mt-2">Application des règles tarifaires</p>
        </div>
      </div>
    );
  }

  const priceDetails = priceResult?.price_details;

  return (
    <div className="space-y-6" data-testid="booking-step-4">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-['Playfair_Display'] text-[#E5E5E5] mb-2">
          Votre tarif
        </h2>
        <p className="text-[#E5E5E5]/60 text-sm">
          Prix calculé selon nos règles tarifaires
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-medium">Erreur de tarification</p>
            <p className="text-sm opacity-80">{error}</p>
          </div>
        </div>
      )}

      {/* Main Price Card */}
      {priceDetails && (
        <Card className="bg-gradient-to-br from-[#C9A24D]/20 to-[#1F1F1F] border-[#C9A24D]/30 overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-[#E5E5E5] text-center">
              <span className="text-5xl font-bold text-[#C9A24D]" data-testid="total-price">
                {Math.round(priceDetails.total_ttc)}€
              </span>
              <span className="text-[#E5E5E5]/60 text-lg ml-2">TTC</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            {/* Pricing Method Badge */}
            <div className="flex justify-center mb-4">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#C9A24D]/20 rounded-full text-[#C9A24D] text-sm">
                {getPricingMethodIcon(priceDetails.pricing_method?.selected_type)}
                <span>{getPricingMethodLabel(priceDetails.pricing_method?.selected_type)}</span>
              </div>
            </div>

            {/* Surcharges Display - Clear visibility */}
            {priceDetails.surcharges && priceDetails.surcharges.length > 0 && (
              <div className="mb-4 p-3 bg-[#1F1F1F] rounded-lg border border-[#C9A24D]/20" data-testid="surcharges-section">
                <div className="flex items-center gap-2 mb-2">
                  <Moon className="w-4 h-4 text-[#C9A24D]" />
                  <span className="text-sm font-medium text-[#E5E5E5]">Suppléments appliqués</span>
                </div>
                <div className="space-y-2">
                  {priceDetails.surcharges.map((surcharge, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between text-sm"
                      data-testid={`surcharge-item-${index}`}
                    >
                      <div className="flex items-center gap-2">
                        {surcharge.type === 'night' && <Moon className="w-3.5 h-3.5 text-indigo-400" />}
                        {surcharge.type === 'weekend' && <Calendar className="w-3.5 h-3.5 text-orange-400" />}
                        {surcharge.type === 'holiday' && <Calendar className="w-3.5 h-3.5 text-red-400" />}
                        {!['night', 'weekend', 'holiday'].includes(surcharge.type) && <Percent className="w-3.5 h-3.5 text-[#C9A24D]" />}
                        <span className="text-[#E5E5E5]/80">{surcharge.name}</span>
                        {surcharge.rate && (
                          <span className="text-[#E5E5E5]/50 text-xs">({surcharge.rate}%)</span>
                        )}
                      </div>
                      <span className="text-[#C9A24D] font-medium">+{surcharge.amount?.toFixed(2) || 0}€</span>
                    </div>
                  ))}
                </div>
                {priceDetails.surcharges_total > 0 && (
                  <div className="mt-2 pt-2 border-t border-[#E5E5E5]/10 flex justify-between text-sm">
                    <span className="text-[#E5E5E5]/60">Total suppléments</span>
                    <span className="text-[#C9A24D] font-semibold">+{priceDetails.surcharges_total?.toFixed(2)}€</span>
                  </div>
                )}
              </div>
            )}

            {/* Night/Weekend Info Badge if applicable */}
            {priceDetails.inputs?.datetime?.time_slot === 'night' && (!priceDetails.surcharges || priceDetails.surcharges.length === 0) && (
              <div className="flex justify-center mb-3">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-500/20 rounded-full text-indigo-300 text-xs">
                  <Moon className="w-3.5 h-3.5" />
                  <span>Tarif de nuit applicable</span>
                </div>
              </div>
            )}

            {/* Final Price Confirmation */}
            <div className="flex items-center justify-center gap-2 text-green-400 text-sm">
              <CheckCircle2 className="w-4 h-4" />
              <span>Prix définitif garanti</span>
            </div>
            
            {/* Round-trip price breakdown */}
            {bookingData.is_round_trip && priceDetails && (
              <div className="mt-3 pt-3 border-t border-[#C9A24D]/20 space-y-1.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[#E5E5E5]/60">Tarif aller</span>
                  <span className="text-[#E5E5E5]">{priceDetails.price_aller?.toFixed(2) || (priceDetails.total_ttc / (2 - (priceDetails.return_discount_pct || 10) / 100)).toFixed(2)}€</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-cyan-400/70">Tarif retour <span className="text-[10px]">(-{priceDetails.return_discount_pct || 10}%)</span></span>
                  <span className="text-cyan-400">{priceDetails.price_retour?.toFixed(2) || ((priceDetails.total_ttc / (2 - (priceDetails.return_discount_pct || 10) / 100)) * (1 - (priceDetails.return_discount_pct || 10) / 100)).toFixed(2)}€</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-[#C9A24D]/10">
                  <span className="text-[#E5E5E5] font-semibold">Total A/R</span>
                  <span className="text-[#C9A24D] font-bold">{Math.round(priceDetails.total_ttc)}€</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Trip Summary */}
      <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
        <CardContent className="p-4 space-y-3">
          <h3 className="text-[#C9A24D] font-medium mb-3">
            {bookingData.service_type === 'hourly' ? 'Récapitulatif mise à disposition' : 'Récapitulatif du trajet'}
          </h3>
          
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
            </div>
            <div className="flex-1">
              <p className="text-xs text-[#E5E5E5]/40">
                {bookingData.service_type === 'hourly' ? 'Prise en charge' : 'Départ'}
              </p>
              <p className="text-sm text-[#E5E5E5]">{bookingData.pickup?.address || '-'}</p>
            </div>
          </div>

          {/* Afficher l'arrivée seulement si ce n'est pas hourly ou si dropoff est différent de pickup */}
          {(bookingData.service_type !== 'hourly' || 
            (bookingData.dropoff?.address && bookingData.dropoff?.address !== bookingData.pickup?.address)) && (
            <>
              {/* Ligne de connexion */}
              <div className="ml-3 border-l-2 border-dashed border-[#E5E5E5]/20 h-4"></div>
              
              {/* Étapes intermédiaires */}
              {bookingData.intermediate_stops && bookingData.intermediate_stops.length > 0 && (
                <>
                  {bookingData.intermediate_stops.map((stop, index) => (
                    <React.Fragment key={index}>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#C9A24D]/20 flex items-center justify-center flex-shrink-0">
                          <div className="w-2 h-2 rounded-full bg-[#C9A24D]"></div>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-[#E5E5E5]/40">Étape {index + 1}</p>
                          <p className="text-sm text-[#E5E5E5]">{stop.address}</p>
                        </div>
                      </div>
                      <div className="ml-3 border-l-2 border-dashed border-[#E5E5E5]/20 h-4"></div>
                    </React.Fragment>
                  ))}
                </>
              )}
              
              {/* Arrivée */}
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-[#E5E5E5]/40">
                    {bookingData.service_type === 'hourly' ? 'Fin de service' : 'Arrivée'}
                  </p>
                  <p className="text-sm text-[#E5E5E5]">{bookingData.dropoff?.address || '-'}</p>
                </div>
              </div>
            </>
          )}

          {/* Round-trip return summary */}
          {bookingData.is_round_trip && (
            <div className="mt-3 pt-3 border-t border-cyan-500/20">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center">
                  <Navigation className="w-3 h-3 text-cyan-400 rotate-180" />
                </div>
                <span className="text-sm font-medium text-cyan-400">Trajet retour</span>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-[#E5E5E5]/40">Départ retour</p>
                  <p className="text-sm text-[#E5E5E5]">{bookingData.dropoff?.address || '-'}</p>
                </div>
              </div>
              
              <div className="ml-3 border-l-2 border-dashed border-cyan-500/20 h-4"></div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-red-400"></div>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-[#E5E5E5]/40">Arrivée retour</p>
                  <p className="text-sm text-[#E5E5E5]">{bookingData.pickup?.address || '-'}</p>
                </div>
              </div>

              {bookingData.return_datetime && (
                <div className="mt-2 flex items-center gap-2 ml-9">
                  <Calendar className="w-3.5 h-3.5 text-cyan-400/60" />
                  <span className="text-xs text-[#E5E5E5]/50">
                    {new Date(bookingData.return_datetime).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' })}
                    {' à '}
                    {new Date(bookingData.return_datetime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Info spécifique mise à disposition */}
          {bookingData.service_type === 'hourly' && (
            <div className="pt-3 border-t border-[#E5E5E5]/10 space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#C9A24D]" />
                <span className="text-sm text-[#E5E5E5]">
                  Durée : <strong>{bookingData.hours_booked}h</strong>
                </span>
              </div>
              {priceDetails?.base_calculation?.km_included && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[#E5E5E5]/60">
                    Km inclus : {Math.round(priceDetails.base_calculation.km_included)} km
                  </span>
                </div>
              )}
              {priceDetails?.base_calculation?.extra_km > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-orange-400">
                    ⚠️ Km supplémentaires : +{priceDetails.base_calculation.extra_km} km
                    ({bookingData.price_details?.extra_km_price || priceDetails.base_calculation.extra_km_price}€/km)
                  </span>
                </div>
              )}
              {priceDetails?.inputs?.route?.estimated_distance_km > 0 && (
                <div className="text-xs text-[#E5E5E5]/40">
                  Distance estimée : ~{Math.round(priceDetails.inputs.route.estimated_distance_km)} km
                </div>
              )}
            </div>
          )}

          {/* Stats transfert uniquement */}
          {bookingData.service_type !== 'hourly' && (
            <div className="flex items-center justify-between pt-3 border-t border-[#E5E5E5]/10">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#E5E5E5]/40" />
                <span className="text-sm text-[#E5E5E5]/60">
                  ~{priceDetails?.inputs?.route?.estimated_duration_min || 0} min
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Car className="w-4 h-4 text-[#E5E5E5]/40" />
                <span className="text-sm text-[#E5E5E5]/60">
                  ~{priceDetails?.inputs?.route?.estimated_distance_km || 0} km
                </span>
              </div>
            </div>
          )}

          {/* Time indicators */}
          {priceDetails?.inputs?.datetime && (
            <div className="flex items-center gap-4 pt-2 text-xs text-[#E5E5E5]/50">
              {priceDetails.inputs.datetime.is_weekend && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Weekend
                </span>
              )}
              {priceDetails.inputs.datetime.is_holiday && (
                <span className="flex items-center gap-1 text-[#C9A24D]">
                  <Calendar className="w-3 h-3" /> Jour férié
                </span>
              )}
              {priceDetails.inputs.datetime.time_slot === 'night' && (
                <span className="flex items-center gap-1">
                  <Moon className="w-3 h-3" /> Service nuit
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Price Breakdown */}
      {priceDetails && (
        <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
          <CardContent className="p-4">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setShowDetails(!showDetails)}
            >
              <h3 className="text-[#C9A24D] font-medium">Détail du prix</h3>
              {showDetails ? (
                <ChevronUp className="w-5 h-5 text-[#C9A24D]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[#C9A24D]" />
              )}
            </div>
            
            <div className={`space-y-2 text-sm mt-4 ${showDetails ? '' : 'hidden'}`}>
              {/* Breakdown display from API */}
              {priceDetails.breakdown_display?.map((item, index) => (
                <div 
                  key={index} 
                  className={`flex justify-between ${
                    item.is_total ? 'pt-3 border-t border-[#E5E5E5]/20 font-semibold' : ''
                  } ${item.is_discount ? 'text-green-400' : ''} ${item.is_extra ? 'text-orange-400' : ''} ${item.is_km ? 'text-[#E5E5E5]/40 text-xs' : ''} ${item.is_detail ? 'text-[#E5E5E5]/40 text-xs' : ''}`}
                >
                  <span className={`${item.is_total ? 'text-[#E5E5E5]' : ''} ${item.is_extra ? 'text-orange-400' : ''}`}>
                    {item.is_km ? `📍 ${item.label}` : item.is_extra ? `⚠️ ${item.label}` : item.label}
                  </span>
                  <span className={`${item.is_total ? 'text-[#C9A24D]' : ''} ${item.is_extra ? 'text-orange-400 font-medium' : ''}`}>
                    {item.is_km ? `${item.amount} km` : `${item.is_discount ? '-' : ''}${item.amount?.toFixed?.(2) || item.amount}€`}
                  </span>
                </div>
              ))}
            </div>

            {/* Compact view when collapsed */}
            {!showDetails && (
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#E5E5E5]/60">Véhicule</span>
                  <span className="text-[#E5E5E5]">{vehicleNames[bookingData.vehicle_category]}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#E5E5E5]/60">Base</span>
                  <span className="text-[#E5E5E5]">{priceDetails.base_calculation?.base_price?.toFixed(2)}€</span>
                </div>
                {priceDetails.surcharges_total > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#E5E5E5]/60">Suppléments</span>
                    <span className="text-[#E5E5E5]">+{priceDetails.surcharges_total.toFixed(2)}€</span>
                  </div>
                )}
                {priceDetails.options_total > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#E5E5E5]/60">Options</span>
                    <span className="text-[#E5E5E5]">+{priceDetails.options_total.toFixed(2)}€</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-[#E5E5E5]/10 font-semibold">
                  <span className="text-[#E5E5E5]">Total TTC</span>
                  <span className="text-[#C9A24D]">{priceDetails.total_ttc.toFixed(2)}€</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Surcharges Applied */}
      {priceDetails?.surcharges?.length > 0 && (
        <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
          <CardContent className="p-4">
            <h3 className="text-[#C9A24D] font-medium mb-3 flex items-center gap-2">
              <Percent className="w-4 h-4" />
              Suppléments appliqués
            </h3>
            <div className="space-y-2">
              {priceDetails.surcharges.map((surcharge, index) => (
                <div key={index} className="flex justify-between items-center text-sm">
                  <div className="flex items-center gap-2">
                    {surcharge.type === 'night' && <Moon className="w-4 h-4 text-blue-400" />}
                    {surcharge.type === 'weekend' && <Sun className="w-4 h-4 text-orange-400" />}
                    {surcharge.type === 'holiday' && <Calendar className="w-4 h-4 text-red-400" />}
                    {surcharge.type === 'zone' && <MapPinned className="w-4 h-4 text-[#C9A24D]" />}
                    <span className="text-[#E5E5E5]/80">{surcharge.name}</span>
                  </div>
                  <span className="text-[#E5E5E5]">+{surcharge.amount.toFixed(2)}€</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Notice */}
      <div className="flex items-start gap-3 p-4 bg-[#1F1F1F] rounded-lg border border-[#C9A24D]/10">
        <Info className="w-5 h-5 text-[#C9A24D] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[#E5E5E5]/60">
          <p className="mb-1">
            Ce prix est calculé selon nos règles tarifaires officielles et inclut la TVA (10%).
          </p>
          <p className="text-xs text-[#E5E5E5]/40">
            Version tarifaire: {priceDetails?.version || 'N/A'} | 
            Calcul ID: {priceDetails?.calculation_id?.slice(0, 12) || 'N/A'}
          </p>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-4">
        <Button
          variant="outline"
          onClick={prevStep}
          className="flex-1 border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10"
          data-testid="step4-prev-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <Button
          onClick={nextStep}
          disabled={!priceDetails}
          className="flex-1 bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold disabled:opacity-50"
          data-testid="step4-next-button"
        >
          Continuer
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default Step4Price;
